/**
 * Appearance settings store.
 *
 * Deliberately backed by plain `localStorage` rather than the encrypted store
 * introduced for user data. Two reasons, both structural rather than
 * expedient: the pre-paint script in `index.html` must read these
 * synchronously before any JavaScript module has loaded, and a theme choice is
 * not sensitive. Encrypting it would buy nothing and cost a flash of the wrong
 * colours on every launch.
 */

import { create } from 'zustand';
import {
  APPEARANCE_STORAGE_KEY,
  DEFAULT_APPEARANCE,
  parseAppearance,
  resolveAppearance,
  type AppearanceSettings,
  type ResolvedAppearance,
  type SystemPreferences,
} from '@/design/appearance';

const SYSTEM_DEFAULT: SystemPreferences = {
  prefersDark: true,
  prefersReducedMotion: false,
  prefersHighContrast: false,
};

interface AppearanceStore {
  readonly settings: AppearanceSettings;
  readonly system: SystemPreferences;
  readonly resolved: ResolvedAppearance;

  /** Change one setting. Persists immediately; there is no save button. */
  update: <K extends keyof AppearanceSettings>(key: K, value: AppearanceSettings[K]) => void;
  /** Record what the operating system is currently asking for. */
  observeSystem: (system: SystemPreferences) => void;
  reset: () => void;
}

function readStoredSettings(): AppearanceSettings {
  if (typeof localStorage === 'undefined') return DEFAULT_APPEARANCE;
  try {
    return parseAppearance(localStorage.getItem(APPEARANCE_STORAGE_KEY));
  } catch {
    // Private browsing modes and locked-down profiles can throw on access.
    return DEFAULT_APPEARANCE;
  }
}

function persist(settings: AppearanceSettings): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(APPEARANCE_STORAGE_KEY, JSON.stringify(settings));
  } catch {
    // A full or unavailable quota must not break changing the theme.
  }
}

function readSystemPreferences(): SystemPreferences {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return SYSTEM_DEFAULT;
  }
  return {
    prefersDark: window.matchMedia('(prefers-color-scheme: dark)').matches,
    prefersReducedMotion: window.matchMedia('(prefers-reduced-motion: reduce)').matches,
    prefersHighContrast: window.matchMedia('(prefers-contrast: more)').matches,
  };
}

export const useAppearanceStore = create<AppearanceStore>((set) => {
  const settings = readStoredSettings();
  const system = readSystemPreferences();

  return {
    settings,
    system,
    resolved: resolveAppearance(settings, system),

    update: (key, value) =>
      set((state) => {
        const next = { ...state.settings, [key]: value };
        persist(next);
        return { settings: next, resolved: resolveAppearance(next, state.system) };
      }),

    observeSystem: (system) =>
      set((state) => ({ system, resolved: resolveAppearance(state.settings, system) })),

    reset: () =>
      set((state) => {
        persist(DEFAULT_APPEARANCE);
        return {
          settings: DEFAULT_APPEARANCE,
          resolved: resolveAppearance(DEFAULT_APPEARANCE, state.system),
        };
      }),
  };
});

/** The media queries the store listens to, paired with the field each drives. */
export const WATCHED_MEDIA_QUERIES = [
  ['(prefers-color-scheme: dark)', 'prefersDark'],
  ['(prefers-reduced-motion: reduce)', 'prefersReducedMotion'],
  ['(prefers-contrast: more)', 'prefersHighContrast'],
] as const satisfies readonly (readonly [string, keyof SystemPreferences])[];

export { readSystemPreferences };
